import { Icons } from '@/assets';
import IconButton from '@/ctComponents/IconButton';
import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { ReactSVG } from 'react-svg';

const TimePicker = ({
  value,
  onChange,
  placeholder = 'Select time',
  disabled = false,
  error = null,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedTime, setSelectedTime] = useState({
    hour: 8,
    minute: 0,
    amPm: 'AM',
  });
  const [popupPosition, setPopupPosition] = useState({
    top: 0,
    left: 0,
    width: 0,
  });
  const popupRef = useRef(null);
  const inputRef = useRef(null);

  // Parse existing value when component mounts or value changes
  useEffect(() => {
    if (value) {
      const [time, period] = value.split(' ');
      if (time && period) {
        const [hour, minute] = time.split(':');
        setSelectedTime({
          hour: parseInt(hour),
          minute: parseInt(minute) || 0,
          amPm: period,
        });
      }
    }
  }, [value]);

  // Handle outside click to close popup
  useEffect(() => {
    const handleOutsideClick = (e) => {
      if (
        popupRef.current &&
        !popupRef.current.contains(e.target) &&
        inputRef.current &&
        !inputRef.current.contains(e.target)
      ) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleOutsideClick);
    }

    return () => {
      document.removeEventListener('mousedown', handleOutsideClick);
    };
  }, [isOpen]);

  const handleInputClick = () => {
    if (!disabled) {
      if (!isOpen) {
        // Calculate position when opening
        if (inputRef.current) {
          const rect = inputRef.current.getBoundingClientRect();
          setPopupPosition({
            top: rect.bottom + window.scrollY + 4,
            left: rect.left + window.scrollX + rect.width / 2,
            width: rect.width,
          });
        }
        setIsOpen(true);
      } else {
        // Close if already open
        setIsOpen(false);
      }
    }
  };

  const formatTime = (time) => {
    const hour = time.hour === 0 ? 12 : time.hour;
    const minute = time.minute.toString().padStart(2, '0');
    return `${hour}:${minute} ${time.amPm}`;
  };

  const updateTime = (newTime) => {
    setSelectedTime(newTime);
    const formattedTime = formatTime(newTime);
    onChange(formattedTime);
  };

  const incrementHour = () => {
    const newHour = selectedTime.hour === 12 ? 1 : selectedTime.hour + 1;
    updateTime({ ...selectedTime, hour: newHour });
  };

  const decrementHour = () => {
    const newHour = selectedTime.hour === 1 ? 12 : selectedTime.hour - 1;
    updateTime({ ...selectedTime, hour: newHour });
  };

  const incrementMinute = () => {
    const newMinute = selectedTime.minute === 59 ? 0 : selectedTime.minute + 1;
    updateTime({ ...selectedTime, minute: newMinute });
  };

  const decrementMinute = () => {
    const newMinute = selectedTime.minute === 0 ? 59 : selectedTime.minute - 1;
    updateTime({ ...selectedTime, minute: newMinute });
  };

  const toggleAmPm = () => {
    const newAmPm = selectedTime.amPm === 'AM' ? 'PM' : 'AM';
    updateTime({ ...selectedTime, amPm: newAmPm });
  };

  const handleSaveTime = () => {
    const formattedTime = formatTime(selectedTime);
    onChange(formattedTime);
    setIsOpen(false);
  };

  const displayValue = value || placeholder;

  return (
    <div className="relative w-full">
      <div className="relative">
        <input
          ref={inputRef}
          type="text"
          value={displayValue}
          onClick={handleInputClick}
          readOnly
          disabled={disabled}
          placeholder={placeholder}
          className={`
            w-full px-3 h-12 pr-10 border box-border border-solid rounded-md cursor-pointer transition-colors duration-200
            ${error ? 'border-red-500' : 'border-gray-300'}
            ${
              disabled
                ? 'bg-gray-100 cursor-not-allowed'
                : 'bg-white hover:border-primary-text focus:border-primary-text'
            }
            focus:outline-none
          `}
        />
        <div className="absolute right-3 top-1/2 transform -translate-y-1/2 pointer-events-none">
          <svg
            className="w-4 h-4 text-gray-400"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24">
            {isOpen ? (
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M5 15l7-7 7 7"
              />
            ) : (
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M19 9l-7 7-7-7"
              />
            )}
          </svg>
        </div>
      </div>

      {error && (
        <span className="text-red-500 text-sm mt-1 block">{error}</span>
      )}

      {isOpen &&
        createPortal(
          <div
            ref={popupRef}
            className="fixed bg-white border-solid border border-border-primary rounded shadow-md py-1 px-2"
            style={{
              top: popupPosition.top,
              left: popupPosition.left,
              transform: 'translateX(-50%)',
              zIndex: 9999,
            }}>
            <div className="flex flex-col items-center">
              <div className="flex items-center gap-2 mb-2">
                <button
                  type="button"
                  onClick={toggleAmPm}
                  className={`bg-transparent border-solid cursor-pointer w-16 h-5 flex items-center justify-center text-border-secondary border-light-bg flex-1 shadow-none px-4 py-2    ${
                    selectedTime.amPm === 'AM' ? 'bg-light-bg ' : ''
                  } rounded-sm text-xs font-medium transition-colors`}>
                  AM
                </button>{' '}
                <button
                  type="button"
                  onClick={toggleAmPm}
                  className={`bg-transparent text-xs cursor-pointer border-solid w-16 h-5 flex items-center justify-center text-border-secondary border-light-bg flex-1 shadow-none px-4 py-2   ${
                    selectedTime.amPm === 'PM' ? 'bg-light-bg' : ''
                  } rounded-sm font-medium transition-colors`}>
                  PM
                </button>
              </div>
            </div>
            <div className="flex items-center justify-center gap-4">
              {/* Hour Section */}
              <div className="flex flex-col items-center">
                <span className="text-xs text-icon-gray mb-2">Hour</span>
                <div className="flex flex-col  min-w-8 items-center border-solid  border border-border-tertiary rounded-sm">
                  <button
                    type="button"
                    onClick={incrementHour}
                    className="bg-transparent cursor-pointer shadow-none p-0 border-none hover:bg-gray-100 text-gray-600 text-sm">
                    <ReactSVG src={Icons.arrowUpSmall} className="" />
                  </button>
                  <span className="px-2  text-sm font-medium  text-center text-primary-text">
                    {selectedTime.hour}
                  </span>
                  <button
                    type="button"
                    onClick={decrementHour}
                    className="bg-transparent cursor-pointer shadow-none p-0 border-none hover:bg-gray-100 text-gray-600 text-sm">
                    <ReactSVG
                      src={Icons.arrowUpSmall}
                      className="transform rotate-180"
                    />
                  </button>
                </div>
              </div>

              {/* Separator */}
              <div className="text-xl font-bold text-gray-400">:</div>

              {/* Minute Section */}
              <div className="flex flex-col items-center">
                <span className=" text-icon-gray text-xs mb-2">Min</span>
                <div className="flex min-w-8 flex-col items-center border-solid border border-border-tertiary rounded-sm">
                  <button
                    type="button"
                    onClick={incrementMinute}
                    className="bg-transparent cursor-pointer shadow-none p-0 border-none hover:bg-gray-100 text-gray-600 text-sm">
                    <ReactSVG src={Icons.arrowUpSmall} className="" />
                  </button>
                  <span className="px-2  text-sm font-medium  text-center text-primary-text">
                    {selectedTime.minute.toString().padStart(2, '0')}
                  </span>
                  <button
                    type="button"
                    onClick={decrementMinute}
                    className="bg-transparent cursor-pointer shadow-none p-0 border-none hover:bg-gray-100 text-gray-600 text-sm">
                    <ReactSVG
                      src={Icons.arrowUpSmall}
                      className="transform rotate-180"
                    />
                  </button>
                </div>
              </div>

              {/* AM/PM Section */}
            </div>
            <hr className="border-solid border-border-tertiary" />
            {/* Close button */}
            <div className="mt-1 flex justify-between gap-3">
              <IconButton
                text="Close"
                onClick={() => setIsOpen(false)}
                color="#525866"
                style={{ flex: 1, height: 29, borderRadius: 4, fontSize: 12 }}
              />
              <IconButton
                text="Save"
                onClick={handleSaveTime}
                style={{
                  backgroundColor: '#131517',
                  color: '#fff',
                  flex: 1,
                  height: 29,
                  borderRadius: 4,
                }}
              />
            </div>
          </div>,
          document.body
        )}
    </div>
  );
};

export default TimePicker;
